import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../network/axiosClient';

/**
 * Custom hook to fetch and manage system alerts
 * @param {number} pollingInterval - Polling interval in milliseconds (default: 5 minutes)
 * @param {boolean} enablePolling - Whether to enable automatic polling (default: true)
 * @returns {Object} - { alerts, loading, error, refetch }
 */
const useSystemAlerts = (pollingInterval = 5 * 60 * 1000, enablePolling = true) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pollingIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Fetch active alerts from the API
   */
  const fetchAlerts = useCallback(async () => {
    try {
      setError(null);

      const response = await axiosInstance.get('/system-alerts/active');

      if (!isMountedRef.current) return;

      if (response.data.success) {
        setAlerts(response.data.alerts);
      } else {
        setAlerts([]);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      setError(err.message || 'Failed to fetch system alerts');
      setAlerts([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchAlerts();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchAlerts]);

  // Set up polling
  useEffect(() => {
    if (!enablePolling || pollingInterval <= 0) return;

    pollingIntervalRef.current = setInterval(() => {
      fetchAlerts();
    }, pollingInterval);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchAlerts, pollingInterval, enablePolling]);

  return {
    alerts,
    loading,
    error,
    refetch: fetchAlerts
  };
};

export default useSystemAlerts;
